import * as React from "react"
import { MdOpenInNew } from "react-icons/md"
import Loading from "@modules/ui/components/Loading"
import { NarrowSingleColumn } from "@modules/ui/layouts/Containers"
import useProvisioningOptions from "@modules/provisioning/hooks/useProvisioningOptions"
import { useProvisionedSiteDetailsQuery } from "@modules/site/create/Provision/queries.generated"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { CmsVendor, Repository } from "@modules/graphql/types"
import {
  Card,
  CardHeader,
  CardSection,
  Text,
  GitHubIcon,
} from "gatsby-interface"

function getCmsIntegrationTypeName(cmsProvider: CmsVendor) {
  if (cmsProvider === CmsVendor.Contentful) {
    return "space"
  } else if (cmsProvider === CmsVendor.Cosmicjs) {
    return "bucket"
  }
  return "project"
}

function getCmsIntegrationDisplayText(
  cmsProvider: CmsVendor,
  {
    projectName,
    spaceName,
    bucketSlugName,
  }: { projectName?: string; spaceName?: string; bucketSlugName?: string }
): string {
  if (cmsProvider === CmsVendor.Contentful && spaceName) {
    return spaceName
  } else if (cmsProvider === CmsVendor.Cosmicjs && bucketSlugName) {
    return bucketSlugName
  }
  return projectName || ""
}

export function ProvisionSummary({
  organizationId,
  siteId,
}: {
  organizationId: string
  siteId: string
}) {
  const {
    data: provisionedSiteDetails,
    loading: provisionedSiteLoading,
    error: provisionedSiteError,
  } = useProvisionedSiteDetailsQuery({
    variables: { siteId },
    fetchPolicy: `cache-and-network`,
  })

  const {
    provisioningOptions,
    verifyProvisioningOptions,
  } = useProvisioningOptions(organizationId)

  // Make sure we have all provisioning information in session
  verifyProvisioningOptions({ includeStarterProperties: true })

  if (provisionedSiteLoading) {
    return (
      <NarrowSingleColumn>
        <Loading
          delay={1000}
          message="Loading your summary..."
          bufferSize="padded"
          variant="baby"
        />
      </NarrowSingleColumn>
    )
  }

  if (provisionedSiteError) {
    return <ErrorAlert>{provisionedSiteError.message}</ErrorAlert>
  }

  const repositoryFromSite = provisionedSiteDetails?.repositoryFromSite as Repository
  const { nameWithOwner } = repositoryFromSite
  const activeCmsIntegrationConfig =
    provisionedSiteDetails?.activeCmsIntegrationsForSite?.[0]?.config ?? {}

  const {
    cmsProvider,
    cmsProviderLogo,
    cmsProviderFormattedName,
  } = provisioningOptions

  return (
    <Card>
      <CardHeader title="Summary" />
      <CardSection>
        <Text>{`The first build of your project is up and running!`}</Text>
        <Text>{`Here's what we set up for you:`}</Text>
        <div
          css={theme => ({
            display: `flex`,
            alignItems: "center",
            marginBottom: theme.space[7],
          })}
        >
          <GitHubIcon
            size="medium"
            css={theme => ({ marginRight: theme.space[3] })}
          />
          <Text noMarginBottom>
            A Github repository at{" "}
            <a
              href={`https://github.com/${nameWithOwner}`}
              target="_blank"
              rel="noreferrer"
            >
              {nameWithOwner}
            </a>{" "}
            <MdOpenInNew />
          </Text>
        </div>
        {cmsProvider !== CmsVendor.None && (
          <div css={{ display: `flex`, alignItems: "center" }}>
            <img
              height={25}
              src={cmsProviderLogo}
              alt={`${cmsProvider} logo`}
              css={theme => ({ marginRight: theme.space[3] })}
            />
            <Text>
              {`A ${cmsProviderFormattedName} ${getCmsIntegrationTypeName(
                cmsProvider
              )} called `}
              <a
                href={activeCmsIntegrationConfig.managementUrl || ``}
                target="_blank"
                rel="noreferrer"
              >{`${getCmsIntegrationDisplayText(
                cmsProvider,
                activeCmsIntegrationConfig
              )}`}</a>{" "}
              <MdOpenInNew />
            </Text>
          </div>
        )}
      </CardSection>
    </Card>
  )
}
